import { getBackendOptions, MultiBackend, Tree } from '@minoru/react-dnd-treeview';
import { useState } from 'react';
import { DndProvider } from 'react-dnd';
import SimpleBar from 'simplebar-react';
import { Modal } from '../../../../common/components/modals';
import { GroupViewModel } from '../../models/GroupTree';
import { GroupModel } from '../../models/payrollNoveltyFlow';
import { GroupCustomNode } from '../tree/GroupCustomNode';

export interface AddGroupTreeModalProps {
    isOpen: boolean;
    onConfirm: (data: GroupViewModel[]) => void;
    onClose: () => void;
    data: GroupViewModel[];
    dataSelected: GroupModel[];
}

export const AddGroupTreeModal = ({
    onClose,
    onConfirm,
    isOpen,
    data,
    dataSelected,
}: AddGroupTreeModalProps) => {
    const [groupsTree, setGroupsTree] = useState<GroupViewModel[]>(
        data
            ? [
                  ...data?.map((t) => {
                      return {
                          ...t,
                          parent: t.parent ? t.parent : 0,
                      };
                  }),
              ]
            : []
    );

    let initialData: GroupViewModel[] = dataSelected?.map((d) => {
        return {
            id: d.groupId,
            text: d.groupName,
            droppable: false,
            parent: 0,
        };
    });

    const [selectedGroups, setSelectedGroups] = useState<GroupViewModel[]>([
        ...initialData,
    ]);
    const [searchQuery, setSearchQuery] = useState('');

    const handlerSubmit = () => {
        onConfirm(selectedGroups);
    };

    // Función para obtener todos los hijos y los hijos de los hijos de un grupo
    const getAllDescendants = (
        parentId: number,
        groups: GroupViewModel[]
    ): GroupViewModel[] => {
        // Obtener los hijos directos del nodo
        const directChildren = groups?.filter((group) => group.parent === parentId);
        let descendants = [...directChildren];

        // Recursivamente agregar los hijos de los hijos
        directChildren?.forEach((child) => {
            descendants = [
                ...descendants,
                ...getAllDescendants(Number(child.id), groups),
            ];
        });

        return descendants;
    };

    const handlerSelectedNode = (node: GroupViewModel) => {
        const descendants = getAllDescendants(Number(node.id), groupsTree);
        const filteredDescendants = descendants.filter((d) => d.isAvailable);

        const alreadySelected = selectedGroups?.some(
            (group) => group.id === node.id
        );

        if (alreadySelected) {
            // ✅ Permite deseleccionar incluso si no está disponible
            const newSelectedGroups = selectedGroups?.filter(
                (group) =>
                    !filteredDescendants.some(
                        (descendant) => descendant.id === group.id
                    ) && group.id !== node.id
            );
            setSelectedGroups(newSelectedGroups);
        } else {
            // ❌ No permitir seleccionar si no está disponible
            if (!node.isAvailable) return;

            setSelectedGroups((prevSelectedGroups) => {
                const newNodes = [node, ...filteredDescendants];
                const combined = [...prevSelectedGroups, ...newNodes];
                const uniqueNodes = Array.from(
                    new Map(combined.map((item) => [item.id, item])).values()
                );
                return uniqueNodes;
            });
        }
    };

    const handleDrop = () => {};

    // Filtrar grupos según el criterio de búsqueda
    const filteredGroups = groupsTree.filter((group) => {
        // Si el nombre del grupo coincide con el texto de búsqueda, o si tiene hijos que coinciden
        const descendants = getAllDescendants(Number(group.id), groupsTree);
        const childrenMatch = descendants.some((child) =>
            child.text.toLowerCase().includes(searchQuery.toLowerCase())
        );

        // Si el padre coincide directamente o tiene hijos que coinciden, lo incluimos
        return (
            group.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
            childrenMatch
        );
    });

    return (
        <Modal
            isOpen={isOpen}
            modalTitle='Agregar grupos'
            disableSubmit={false}
            onSubmit={handlerSubmit}
            onClose={onClose}
            widthCustom='700px'
        >
            <div>
                <div
                    className='mb-2'
                    style={{
                        background: '#fff',
                        position: 'relative',
                        width: '50%',
                    }}
                >
                    <i
                        className='ri-search-line'
                        style={{
                            position: 'absolute',
                            top: '25%',
                            left: '10px',
                            color: '#9F9FA0',
                        }}
                    ></i>
                    <input
                        name='search'
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        type='text'
                        placeholder='Buscar...'
                        className='form-control  w-100 text-dark'
                        style={{
                            background: 'transparent',
                            paddingLeft: '25px',
                            fontSize: '11px',
                        }}
                        autoSave={'false'}
                        autoComplete={'false'}
                    />
                    {searchQuery.trim().length > 0 && (
                        <span
                            className='mdi mdi-close-circle search-widget-icon search-widget-icon-close'
                            id='search-close-options'
                            onClick={() => setSearchQuery('')}
                            style={{
                                position: 'absolute',
                                right: 10,
                                top: '20%',
                                fontSize: 12.5,
                                color: '#9F9FA0',
                                cursor: 'pointer',
                            }}
                        ></span>
                    )}
                </div>

                <SimpleBar
                    direction='rtl'
                    style={{
                        width: '100%',
                        maxHeight: 'calc(100vh - 200px)',
                        height: 'calc(100vh - 200px)',
                    }}
                >
                    <div className='no-listing'>
                        <DndProvider
                            backend={MultiBackend}
                            options={getBackendOptions()}
                        >
                            <Tree
                                sort={false}
                                tree={filteredGroups}
                                rootId={0}
                                render={(
                                    node: GroupViewModel,
                                    { depth, isOpen, onToggle }
                                ) => (
                                    <GroupCustomNode
                                        node={node}
                                        depth={depth}
                                        isOpen={isOpen}
                                        isSelected={selectedGroups.some(
                                            (item) => item.id === node.id
                                        )}
                                        onToggle={onToggle}
                                        onSelect={handlerSelectedNode}
                                        searchValue={searchQuery}
                                    />
                                )}
                                onDrop={handleDrop}
                            />
                        </DndProvider>
                    </div>
                </SimpleBar>
            </div>
        </Modal>
    );
};
